// FLAG:docuAiEnhancements — turns the open document into the receipt chat's SITUATION block
// ("what is in front of the user"). Pure and additive: it only READS the file the drawer
// already holds (metaData.ocr, documentInvoiceDetails, validationResult, eInvoice) and
// never triggers a fetch.
//
// WHY this exists: without it the chat knew only the file NAME, so every question about the
// document itself ("is this invoice valid or complete now?") was answered with a
// counter-question — the model asked the reviewer to read the screen back to it. The facts
// below are exactly what the reviewer sees, so the answer can be given straight away.
//
// The German wording of the STATE fields lives in the gateway (it serves every host of the
// chat); this module sends codes for those and pre-formatted "Label: Value" lines for the
// extracted values, because formatting amounts/dates/currency is a client concern.
import moment from 'moment'
import config from '@/config'
import lang from '@/lang'
import formatNumber from '@/modules/react/util/formatNumber'
import {
  getInvoiceAnalysisState,
  hasInvalidValidationResult,
  hasXmlInvoiceAnalysis,
  isValidValidationValue,
  INVOICE_ANALYSIS_STATE,
} from '@/modules/document/utils/invoice-analysis-state'
import { getEInvoiceFormatLabel } from '@/modules/document/utils/e-invoice-format'
import { getFlaggedFields } from '@/modules/document/components/review/field-validation'
import {
  readDefaultTaxCategory,
  selectTenantTaxCategories,
  TaxCategoryAccount,
} from '@/modules/document/utils/tax-category-situation'

// Mirrors LlmRagChatSituationDto in dms-s3 (libs/dtos/src/apiDtos/llm-rag-chat.dto.ts).
// Keep the two in sync — the gateway drops anything it does not know.
export interface ChatSituation {
  view?: string
  documentName?: string
  documentKind?: string
  analysisState?: string
  invoiceSource?: 'xml' | 'scan' | 'unknown'
  validationState?: 'valid' | 'invalid' | 'unknown' | 'notApplicable'
  eInvoiceFormat?: string
  facts?: string[]
  positions?: string[]
  supplierAddress?: string
  hints?: string[]
  missingFields?: string[]
  uncertainFields?: string[]
  taxCategories?: string[]
  defaultTaxCategory?: string
}

/** Optional tenant master data the caller passes in. Kept out of the file-derived part on
 * purpose: everything else in this module is READ from the open document, while the chart
 * of accounts belongs to the tenant and comes from the taxCategories duck. */
export interface ChatSituationOptions {
  taxCategoryAccounts?: TaxCategoryAccount[] | null
}

// The analysis states the gateway knows. A state it does not know (or null) is simply not
// sent — a state code the model cannot interpret is worse than no state at all.
const REPORTABLE_ANALYSIS_STATES: string[] = [
  INVOICE_ANALYSIS_STATE.ANALYZING,
  INVOICE_ANALYSIS_STATE.REVIEW,
  INVOICE_ANALYSIS_STATE.COMPLETE,
  INVOICE_ANALYSIS_STATE.PARTIAL,
  INVOICE_ANALYSIS_STATE.INVALID,
  INVOICE_ANALYSIS_STATE.FAILED,
  INVOICE_ANALYSIS_STATE.NO_INVOICE,
]

// German labels for the analysis fields, used for BOTH the "please check" list and the
// list of values the analysis could not determine. Keys are the fieldMeta/extraction keys
// DocuAI stamps; an unknown key falls back to itself so a new DocuAI field still shows up
// (as its raw name) instead of silently disappearing.
//
// READ from `lang` wherever the review renders the field, never re-typed: these names send
// the reviewer to a field on their SCREEN, so a second hand-written spelling is a defect
// waiting to happen — and it happened. This module called `date` „Rechnungsdatum" while the
// review form has always labelled it „Belegdatum", so the chat pointed at a field that does
// not exist under that name; the reviewer answered, verbatim, „QUATSCH" (DMS issue
// a7285ab1 / casebook 0085). The two label sources for a review field are
// `lang.receipt.form.*` (receipt header, rendered by receipt-form.tsx) and
// `lang.documents.reviewPaymentStatus.fields` (the „Bezahlung" card, review-payment-fields.tsx).
// Deliberate exceptions, i.e. do NOT "fix" these to the lang value:
//  - payeeName: the form label is „(L)ieferant / (K)unde" — the parentheses are keyboard
//    hints, not part of the name, and would read as noise in a sentence.
//  - accountHolder/currency/priceNet/…: not rendered as their own labelled review field.
const receiptFormLabels = lang.receipt.form
const paymentFieldLabels = lang.documents.reviewPaymentStatus.fields

const FIELD_LABELS: Record<string, string> = {
  accountHolder: 'Kontoinhaber',
  bankInstitute: paymentFieldLabels.bankInstitute,
  bic: paymentFieldLabels.bic,
  currency: 'Währung',
  date: receiptFormLabels.date,
  debitIbans: paymentFieldLabels.debitIban,
  deliveryDate: receiptFormLabels.deliveryDate,
  iban: paymentFieldLabels.iban,
  invoiceNumber: receiptFormLabels.receiptNumber,
  openAmount: paymentFieldLabels.openAmount,
  paymentDueDate: 'Fälligkeitsdatum',
  paymentMethodCode: paymentFieldLabels.method,
  paymentStatus: paymentFieldLabels.status,
  payeeIban: 'IBAN des Lieferanten',
  payeeName: 'Lieferant',
  positions: 'Rechnungspositionen',
  priceNet: 'Nettobetrag',
  priceGross: 'Bruttobetrag',
  priceTotal: 'Gesamtbetrag',
  totalAmount: 'Gesamtbetrag',
  vat: 'Steuersatz',
  vatAmount: 'Umsatzsteuerbetrag',
}

const labelFor = (field: string) => FIELD_LABELS[field] ?? field

const firstValue = (...values: unknown[]) =>
  values.find(value => value !== null && value !== undefined && value !== '')

const asNumber = (value: unknown): number | null => {
  const num = Number(typeof value === 'object' && value !== null ? (value as { value?: unknown }).value : value)
  return Number.isFinite(num) ? num : null
}

const formatAmount = (value: unknown, currency: string) => {
  const num = asNumber(value)
  if (num === null) return null
  return `${formatNumber(num, config.currencyFormat)} ${currency}`
}

const formatDay = (value: unknown) => {
  if (typeof value !== 'string' || !value) return null
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format(config.dateFormat.client) : null
}

interface InvoiceValues {
  supplierName?: unknown
  invoiceNumber?: unknown
  invoiceDate?: unknown
  deliveryDate?: unknown
  dueDate?: unknown
  priceNet?: unknown
  vatAmount?: unknown
  vat?: unknown
  totalAmount?: unknown
  currency: string
  // The line items themselves, not just how many there are. Sending only the count is what
  // DMS issue cfcb4252 / casebook 0084 reported: the chat could not say a word about the
  // positions though the client had them all along ("available" doesn't mean "in the prompt").
  positions: unknown[]
}

// Invoice details the analysis extracts and that a reviewer would look for on ANY invoice
// (§ 14 UStG's core set, minus what we do not extract — e.g. the supplier's tax number).
// Reported as "not determined", never as "missing on the document": the analysis not finding
// a value says nothing about the paper. The gateway wording states that explicitly.
const CORE_FIELDS: Array<{ key: string, value: (invoice: InvoiceValues) => unknown }> = [
  { key: 'payeeName', value: invoice => invoice.supplierName },
  { key: 'invoiceNumber', value: invoice => invoice.invoiceNumber },
  { key: 'date', value: invoice => invoice.invoiceDate },
  { key: 'priceNet', value: invoice => invoice.priceNet },
  { key: 'vatAmount', value: invoice => invoice.vatAmount },
  { key: 'totalAmount', value: invoice => invoice.totalAmount },
]

const readInvoiceValues = (file): InvoiceValues => {
  const details = file?.documentInvoiceDetails || {}
  const ocr = file?.metaData?.ocr || {}
  const positions = details.positions ?? ocr.positions

  return {
    supplierName: firstValue(ocr.payeeName, ocr.payee, details.accountHolder),
    invoiceNumber: firstValue(details.invoiceNumber, ocr.receiptNumber),
    invoiceDate: firstValue(details.date, ocr.date),
    deliveryDate: firstValue(ocr.deliveryDate),
    dueDate: firstValue(details.paymentDueDate, ocr.paymentDueDate),
    priceNet: firstValue(details.priceNet, ocr.priceNet),
    vatAmount: firstValue(details.vatAmount, ocr.vatAmount),
    vat: firstValue(details.vat, ocr.vat),
    totalAmount: firstValue(details.totalAmount, ocr.priceTotal, ocr.priceGross),
    currency: `${firstValue(details.currency, ocr.currency) ?? 'EUR'}`,
    positions: Array.isArray(positions) ? positions : [],
  }
}

// The facts the analysis attached to the whole invoice. Read through one helper because two
// consumers need it (the hints below and the positions' net/gross basis) and the three places
// it can sit are an artefact of how the analysis was persisted over time.
const readInvoiceFacts = (file) => file?.documentInvoiceDetails?.invoiceFacts
  ?? file?.metaData?.invoiceFacts
  ?? file?.metaData?.ocr?.invoiceFacts

// ─── Invoice line items ─────────────────────────────────────────────────────────────────────
// Which side of a line the DOCUMENT prints. calculatePosition fills BOTH sides of every
// position (one is always derived from the other), so the numbers alone cannot tell them
// apart — only `invoiceFacts.taxCalculation.lineTotalsAreGross` can. Presenting the derived
// side as if it were printed is the error class of casebook 0081, so 'unknown' names no side
// at all and the gateway's positions rule forbids the model from asserting one.
type LineBasis = 'net' | 'gross' | 'unknown'

const BASIS_LABEL: Record<LineBasis, string> = { gross: ' brutto', net: ' netto', unknown: '' }

const readLineBasis = (file): LineBasis => {
  const lineTotalsAreGross = readInvoiceFacts(file)?.taxCalculation?.lineTotalsAreGross
  if (lineTotalsAreGross === true) return 'gross'
  if (lineTotalsAreGross === false) return 'net'
  return 'unknown'
}

// Mirrors @ArrayMaxSize(30)/@MaxLength(200) on LlmRagChatSituationDto.positions in dms-s3.
const MAX_POSITIONS = 30
const MAX_POSITION_LINE = 200
const MAX_POSITION_DESCRIPTION = 120

const truncate = (text: string, limit: number) => (text.length <= limit
  ? text
  : `${text.slice(0, Math.max(0, limit - 1)).trimEnd()}…`)

const formatQuantity = (value: number) => (Number.isInteger(value)
  ? `${value}`
  : formatNumber(value, config.currencyFormat))

/** The UNIT price as printed, i.e. before a line discount; the line total below is the
 * effective one, and the "… % discount" part explains the difference. */
const readPositionUnitPrice = (position, basis: LineBasis) => (basis === 'gross'
  ? firstValue(position?.priceGross, position?.priceGrossAfterDiscount)
  : firstValue(position?.priceNet, position?.priceNetAfterDiscount))

const readPositionTotal = (position, basis: LineBasis) => (basis === 'gross'
  ? firstValue(position?.totalGrossAfterDiscount, position?.totalGross)
  : firstValue(position?.totalNetAfterDiscount, position?.totalNet))

// A line the extraction could not price is KEPT at 0,00 and flagged (contractToAnalysis'
// MISSING_AMOUNT_LEAF). Sending that 0,00 would put an amount in the prompt that stands on no
// document — the same claim the missingFields wording is careful not to make. fieldMeta
// .positions is index-aligned with the built positions (the review reads it the same way).
const hasUnreadableAmount = (file, index: number): boolean => {
  const positionsMeta = file?.documentInvoiceDetails?.fieldMeta?.positions
  return Array.isArray(positionsMeta)
    && positionsMeta[index]?.price?.validationCode === 'line_amount_missing'
}

const describePosition = (position): string => {
  const description = typeof position?.description === 'string' ? position.description.trim() : ''
  return description || '(ohne Bezeichnung)'
}

/**
 * One position as the single line the model gets:
 * "1. Batterien AA · 2 × 12,34 EUR · 24,68 EUR brutto · 19 % USt".
 *
 * Only the DESCRIPTION is shortened, never the assembled line: cutting from the right would
 * drop the amounts and hand the model a position without a price (casebook 0084). The
 * description budget is therefore whatever is left of the 200-character cap after the figures.
 */
const formatPositionLine = (
  position,
  index: number,
  currency: string,
  basis: LineBasis,
  amountUnreadable: boolean,
): string => {
  const quantity = asNumber(position?.amount)
  const unitPrice = formatAmount(readPositionUnitPrice(position, basis), currency)
  const total = formatAmount(readPositionTotal(position, basis), currency)
  const vatPercent = asNumber(position?.vatPercent)
  const discountPercent = asNumber(position?.discountPercent)

  const parts: string[] = []
  if (quantity !== null && quantity !== 1) {
    // The unit price is suppressed together with the line total on an unpriced line: it is
    // the same placeholder 0,00, and stating it per unit would be the same false claim.
    parts.push(unitPrice && !amountUnreadable
      ? `${formatQuantity(quantity)} × ${unitPrice}`
      : `Menge ${formatQuantity(quantity)}`)
  }
  if (amountUnreadable) parts.push('Betrag nicht erkannt')
  else if (total) parts.push(`${total}${BASIS_LABEL[basis]}`)
  if (vatPercent !== null) parts.push(`${vatPercent} % USt`)
  if (discountPercent) parts.push(`${discountPercent} % Rabatt`)

  const prefix = `${index + 1}. `
  const suffix = parts.length ? ` · ${parts.join(' · ')}` : ''
  const budget = Math.min(MAX_POSITION_DESCRIPTION, MAX_POSITION_LINE - prefix.length - suffix.length)

  return `${prefix}${truncate(describePosition(position), Math.max(0, budget))}${suffix}`
}

const buildPositionLines = (file, invoice: InvoiceValues): string[] => {
  if (!invoice.positions.length) return []

  const basis = readLineBasis(file)
  // One slot stays free for the truncation marker. The marker has to be built HERE because
  // only this side knows the real count — the gateway sees the already-capped list — and a
  // capped list without it reads as the complete one (casebook 0076: what stands in the
  // prompt is an assertion). The gateway's positions rule then makes the model say so.
  const shown = invoice.positions.length > MAX_POSITIONS
    ? invoice.positions.slice(0, MAX_POSITIONS - 1)
    : invoice.positions
  const lines = shown.map((position, index) => formatPositionLine(
    position, index, invoice.currency, basis, hasUnreadableAmount(file, index),
  ))

  if (shown.length < invoice.positions.length) {
    lines.push(`… (Liste gekürzt: ${shown.length} von ${invoice.positions.length} Positionen aufgeführt)`)
  }
  return lines
}

// ─── Supplier address ───────────────────────────────────────────────────────────────────────
const MAX_SUPPLIER_ADDRESS = 200

const addressText = (value: unknown): string => (typeof value === 'string' || typeof value === 'number'
  ? `${value}`.trim()
  : '')

/**
 * The supplier's postal address as one line, so the chat can do more than name the supplier
 * (DMS report cfcb4252; a letter without a recipient is not a letter).
 *
 * Origin-NEUTRAL by construction, and the gateway wording says so: the reviewer's own
 * correction wins, then whatever the analysis left in payeeAddress — which omxweb overwrites
 * from the supplier/customer directory when it matched one (analyzeFile) — then the flat
 * legacy fields. Both key spellings are accepted: the analysis writes `zip`, directory
 * records write `zipCode`.
 */
const readSupplierAddress = (file, fallbackName: unknown): string | null => {
  const details = file?.documentInvoiceDetails || {}
  const ocr = file?.metaData?.ocr || {}
  const reviewPayee = details.reviewPayee?.version === 1 ? details.reviewPayee : null
  const address = reviewPayee?.address
    ?? ocr.payeeAddress
    ?? ocr.ocrSupplier?.addresses?.[0]
    ?? { street: ocr.payeeStreet, zip: ocr.payeeZipCode, city: ocr.payeeCity, countryIso: ocr.countryCode }

  const street = addressText(address?.street)
  const zip = addressText(address?.zip ?? address?.zipCode)
  const city = addressText(address?.city)
  // A name on its own is already in the facts as "Supplier: …" — without a street or a
  // place there is no address to add.
  if (!street && !zip && !city) return null

  const name = addressText(reviewPayee
    ? (reviewPayee.companyName || [reviewPayee.firstName, reviewPayee.lastName].filter(Boolean).join(' '))
    : (address?.companyName ?? address?.name ?? fallbackName))
  const country = addressText(address?.countryIso ?? address?.country ?? address?.countryCode)

  return [name, street, [zip, city].filter(Boolean).join(' '), country]
    .filter(Boolean)
    .join(', ')
    .slice(0, MAX_SUPPLIER_ADDRESS)
}

// XML vs. paper/PDF — the distinction that decides which answers are even possible. Only
// reported once an analysis result exists; during the analysis window we do not yet know.
const readInvoiceSource = (file): ChatSituation['invoiceSource'] => {
  if (hasXmlInvoiceAnalysis(file)) return 'xml'
  if (file?.metaData?.ocr && !file?.metaData?.ocr?.isRunning) return 'scan'
  return 'unknown'
}

const readValidationState = (file, source: ChatSituation['invoiceSource']): ChatSituation['validationState'] => {
  // A document without structured e-invoice data never gets a verdict — and saying so is a
  // complete answer, not a gap. This is the case the chat used to get wrong.
  if (source === 'scan') return 'notApplicable'
  if (source !== 'xml') return undefined
  if (isValidValidationValue(file?.validationResult?.valid)) return 'valid'
  if (hasInvalidValidationResult(file)) return 'invalid'
  return 'unknown'
}

// The advisory facts the review itself surfaces, so the chat says the same thing the screen
// does. Only facts the analysis actually produced — nothing is derived here.
const readHints = (file): string[] => {
  const invoiceFacts = readInvoiceFacts(file)
  const hints: string[] = []

  if (invoiceFacts?.conversionRequired === true) {
    const original = [invoiceFacts.originalGrossAmount, invoiceFacts.originalCurrency].filter(Boolean).join(' ')
    hints.push(`Fremdwährungsbeleg${original ? ` über ${original}` : ''}: die Beträge sind keine Euro-Beträge.`)
  }
  if (invoiceFacts?.vatJurisdiction?.bookable === false) {
    const rates = (invoiceFacts.vatJurisdiction.unbookableRates || []).join(', ')
    hints.push(
      `Der Beleg weist einen Steuersatz aus, der in orgaMAX nicht gebucht werden kann${rates ? ` (${rates})` : ''}`
      + ' — es lässt sich deshalb keine Steuerkategorie zuweisen.',
    )
  }
  const errorCount = file?.eInvoice?.errorCount
  if (typeof errorCount === 'number' && errorCount > 0) {
    hints.push(`Die E-Rechnungs-Prüfung hat ${errorCount} Fehler gemeldet.`)
  }
  return hints
}

const MAX_FACTS = 25
const MAX_LIST = 15
const MAX_HINTS = 10

/**
 * Build the situation block for the open document. Returns undefined when there is no
 * document — the chat then behaves exactly as it did before this block existed.
 */
export const buildChatSituation = (
  file,
  view?: string,
  options: ChatSituationOptions = {},
): ChatSituation | undefined => {
  if (!file) return undefined

  const invoice = readInvoiceValues(file)
  const source = readInvoiceSource(file)
  const analysisState = getInvoiceAnalysisState(file)
  const details = file?.documentInvoiceDetails || {}

  const facts = [
    // Through labelFor wherever the value has its own field in the review, so a fact and the
    // "please check"/"not determined" lists below never name the same field differently. The
    // amount labels stay hand-written on purpose: „Gesamtbetrag (brutto)" says which side the
    // figure is, which the form's own label does not (net-vs-gross discipline of casebook 0081).
    [labelFor('payeeName'), invoice.supplierName],
    [labelFor('invoiceNumber'), invoice.invoiceNumber],
    [labelFor('date'), formatDay(invoice.invoiceDate)],
    [labelFor('deliveryDate'), formatDay(invoice.deliveryDate)],
    ['Fällig am', formatDay(invoice.dueDate)],
    ['Nettobetrag', formatAmount(invoice.priceNet, invoice.currency)],
    ['Umsatzsteuer', formatAmount(invoice.vatAmount, invoice.currency)],
    ['Steuersatz', invoice.vat === null || invoice.vat === undefined ? null : `${invoice.vat} %`],
    ['Gesamtbetrag (brutto)', formatAmount(invoice.totalAmount, invoice.currency)],
    ['Währung', invoice.currency],
    // Stays as the HEADING for the position list below — it is the only place that states the
    // true count when that list had to be shortened.
    ['Rechnungspositionen', invoice.positions.length ? `${invoice.positions.length}` : null],
  ]
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([label, value]) => `${label}: ${value}`)
    .slice(0, MAX_FACTS)

  // Only report gaps once there IS a result to have gaps in: while the analysis runs, every
  // field is "missing" and listing them would be plainly wrong.
  const hasResult = !!file?.metaData?.ocr && !file?.metaData?.ocr?.isRunning
  const missingKeys: string[] = hasResult
    ? CORE_FIELDS
      .filter(({ value }) => {
        const raw = value(invoice)
        return raw === null || raw === undefined || raw === ''
      })
      .map(({ key }) => key)
    : []
  const missingFields = missingKeys.map(labelFor).slice(0, MAX_LIST)

  // A field WITHOUT a value cannot also be "only detected with low confidence" — and the gateway words the
  // two lists as facts that then contradict each other ("flagged for review" next to
  // "could not be determined by the analysis", rag-chat.service.ts). DocuAI stamps a required field
  // it did not find as validationState 'error' with code 'required_missing', so it landed in
  // BOTH lists and the model merged them into one self-contradicting sentence — reported as
  // „QUATSCH" (DMS issue ref. a7285ab1/casebook 0085). "Not determined" wins: it's the
  // accurate half and the one the reviewer can act on.
  //
  // Two rules, because they catch different fields: the key set covers the CORE_FIELDS whose
  // value we can see here, `required_missing` covers every other field DocuAI marks as a
  // missing requirement (paymentDueDate, iban, …) that this module never reads a value for.
  const isMissing = new Set(missingKeys)
  const uncertainFields = getFlaggedFields(details.fieldMeta)
    .filter(({ field }) => !isMissing.has(field)
      && details.fieldMeta?.[field]?.validationCode !== 'required_missing')
    .map(({ field }) => labelFor(field))
    .filter((label, index, all) => all.indexOf(label) === index)
    .slice(0, MAX_LIST)

  const situation: ChatSituation = {
    ...(view ? { view } : {}),
    ...(file.name ? { documentName: `${file.name}`.slice(0, 200) } : {}),
    ...(() => {
      const kind = (file.tags || []).find(tag => tag?.isSystem)?.label
      return kind ? { documentKind: kind } : {}
    })(),
    ...(analysisState && REPORTABLE_ANALYSIS_STATES.includes(analysisState) ? { analysisState } : {}),
    ...(source ? { invoiceSource: source } : {}),
    ...(facts.length ? { facts } : {}),
    ...(missingFields.length ? { missingFields } : {}),
    ...(uncertainFields.length ? { uncertainFields } : {}),
  }

  // The line items and the supplier's address: read from the same document the drawer already
  // holds, sent as their OWN fields. Appending them to `facts` would have been the smaller
  // change and the wrong one — the gateway joins `facts` into one line and the slice above
  // cuts from the END, so a long invoice would have dropped supplier/net/VAT/gross.
  const positions = buildPositionLines(file, invoice)
  if (positions.length) situation.positions = positions

  const supplierAddress = readSupplierAddress(file, invoice.supplierName)
  if (supplierAddress) situation.supplierAddress = supplierAddress

  const validationState = readValidationState(file, source)
  if (validationState) situation.validationState = validationState

  const eInvoiceFormat = source === 'xml' ? getEInvoiceFormatLabel(file.eInvoice) : null
  if (eInvoiceFormat) situation.eInvoiceFormat = eInvoiceFormat

  const hints = readHints(file).slice(0, MAX_HINTS)
  if (hints.length) situation.hints = hints

  // Tenant master data: only sent when the caller actually has it loaded. A caller that
  // passes nothing produces exactly the situation this module produced before — the chat
  // then behaves as it did, it just cannot name the tenant's own categories.
  const taxCategories = selectTenantTaxCategories(options.taxCategoryAccounts)
  if (taxCategories.length) situation.taxCategories = taxCategories

  const defaultTaxCategory = readDefaultTaxCategory(file)
  if (defaultTaxCategory) situation.defaultTaxCategory = defaultTaxCategory

  return situation
}

export default buildChatSituation
